/**
 * Stability Results Mock
 *
 * Shows the summary output of a stability analysis:
 * primary cluster parameters, robustness score, and
 * alternate cluster comparison.
 */

const primaryParams = [
  { name: 'Fast EMA', value: '14', range: '12 - 16' },
  { name: 'Slow EMA', value: '29', range: '26 - 32' },
  { name: 'Stop Loss %', value: '2.2', range: '1.8 - 2.8' },
];

const clusters = [
  {
    label: 'Primary',
    points: 12,
    score: 78,
    sharpe: 1.28,
    dd: -18.5,
    color: '#22c55e',
    selected: true,
  },
  {
    label: 'Alternate 1',
    points: 5,
    score: 52,
    sharpe: 1.15,
    dd: -24.2,
    color: '#3b82f6',
    selected: false,
  },
  {
    label: 'Alternate 2',
    points: 4,
    score: 38,
    sharpe: 0.95,
    dd: -28.7,
    color: '#64748b',
    selected: false,
  },
];

function scoreColor(score: number): string {
  if (score >= 70) return '#22c55e';
  if (score >= 50) return '#f59e0b';
  return '#ef4444';
}

export default function StabilityResultsMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Stability Extraction Results
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        {/* Primary cluster parameters */}
        <div style={{
          background: '#1e293b', borderRadius: 4, padding: '0.5rem',
          border: '1px solid #22c55e33', marginBottom: '0.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
            <span style={{ fontSize: '0.5625rem', color: '#22c55e', fontWeight: 700 }}>
              Primary Cluster (Recommended)
            </span>
            <span style={{
              fontSize: '0.75rem', fontWeight: 800, color: '#22c55e',
              fontFamily: 'ui-monospace, monospace',
            }}>
              78
            </span>
          </div>

          {/* Parameter values */}
          {primaryParams.map((p) => (
            <div key={p.name} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '0.2rem',
            }}>
              <span style={{ fontSize: '0.5rem', color: '#94a3b8' }}>{p.name}</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.4375rem', color: '#475569', fontFamily: 'ui-monospace, monospace' }}>
                  range: {p.range}
                </span>
                <span style={{
                  fontSize: '0.625rem', fontWeight: 700, color: '#e5e7eb',
                  fontFamily: 'ui-monospace, monospace',
                  background: '#0f172a', padding: '0.1rem 0.3rem', borderRadius: 3,
                }}>
                  {p.value}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Cluster comparison table */}
        <div style={{
          fontSize: '0.5rem', color: '#64748b', marginBottom: '0.25rem',
          fontFamily: 'ui-monospace, monospace',
        }}>
          <div style={{
            display: 'flex', gap: '0.375rem', borderBottom: '1px solid #334155',
            paddingBottom: '0.2rem', marginBottom: '0.25rem',
          }}>
            <span style={{ width: 72 }}>Cluster</span>
            <span style={{ width: 36, textAlign: 'center' }}>Points</span>
            <span style={{ width: 36, textAlign: 'center' }}>Score</span>
            <span style={{ width: 42, textAlign: 'center' }}>Sharpe</span>
            <span style={{ flex: 1, textAlign: 'center' }}>Robustness</span>
          </div>

          {clusters.map((c) => (
            <div key={c.label} style={{
              display: 'flex', gap: '0.375rem', alignItems: 'center',
              marginBottom: '0.3rem',
              opacity: c.selected ? 1 : 0.65,
            }}>
              <span style={{ width: 72, fontSize: '0.5rem', color: c.color, fontWeight: c.selected ? 700 : 400 }}>
                {c.label}
              </span>
              <span style={{ width: 36, textAlign: 'center', fontSize: '0.5625rem', color: '#e5e7eb' }}>
                {c.points}
              </span>
              <span style={{ width: 36, textAlign: 'center', fontSize: '0.5625rem', fontWeight: 700, color: scoreColor(c.score) }}>
                {c.score}
              </span>
              <span style={{ width: 42, textAlign: 'center', fontSize: '0.5625rem', color: '#94a3b8' }}>
                {c.sharpe}
              </span>
              <div style={{ flex: 1, height: 8, background: '#0f172a', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${c.score}%`,
                  background: `${scoreColor(c.score)}33`,
                  border: `1px solid ${scoreColor(c.score)}55`,
                  borderRadius: 2,
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="platform-mock-caption">
        The centroid of the primary cluster is the recommended parameter set — robust, not just optimal.
      </div>
    </div>
  );
}
